/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  type ReactNode,
  type RefAttributes
} from "react";
import { Button } from "@design-lab/shared/ui";
import { useDropzone } from "react-dropzone";
import {
  useArtwork,
  useArtworkImage,
  useEnhancedImageSource,
  ImageUploadedBanner
} from "@design-lab/entities/artwork";
import { isUserLoggedIn } from "@design-lab/shared/lib/auth";
import { Shirt, Trash2, Camera } from "lucide-react";
import type { Side } from "@design-lab/shared";
import { useTabNavigation } from "@design-lab/shared/hooks/useTabNavigation";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { ARTWORK_PROCESSING } from "@design-lab/shared/constants";
import {
  useShowAiStudio,
  useShowTakePhotoBtn,
  useShowWeaveDesignLab
} from "@design-lab/shared/lib/config";
import { validateDesignLabArtworkFile, DESIGN_LAB_ACCEPT_CONFIG } from "common/artwork-file-types";
import { useNotification } from "@design-lab/shared/ui/notification";
import CameraUploader from "common/mobile-camera-upload";
import { trackTakePhotoCtaClicked } from "@design-lab/shared/lib/analytics";
import { useSearchParams } from "react-router";
import { LoadingBar } from "../../../widgets/design-lab-upload-artwork/ui/LoadingBar";
import { useAutoUploadDetection } from "../../../widgets/design-lab-upload-artwork/model/useAutoUploadDetection";
import { useStartDesigning } from "../../../widgets/design-lab-upload-artwork/model/useStartDesigning";
import { CanvaButton } from "./Canva/CanvaButton";
import { MyDesignLibrary } from "../../../widgets/design-lab-upload-artwork/ui/MyDesignLibrary";
import { AiStudioButton } from "../../../widgets/design-lab-upload-artwork/ui/AiStudioButton";
import { SparkleIcon } from "../../weave-design-lab/ui/components/icons";
import { MENU_IDS } from "../../weave-design-lab/model/state";

export type ImageSelectorProps = {
  side?: Side;
  title?: ReactNode;
  onFileDrop?: (files: File[]) => Promise<void>;
  isLoggedIn?: boolean;
  isDesktop?: boolean;
  shouldOpenLibrary?: boolean;
  onLibraryAutoOpenComplete?: () => void;
  shouldOpenCanvaFromParent?: boolean;
  onCanvaAutoOpenCompleteFromParent?: () => void;
  additionalButton?: ReactNode;
  onCanvaFileReady?: (file: File) => Promise<void>;
  onLibraryDesignSelect?: (artworkId: number) => void;
  isWaitingForCatalogNumber?: boolean;
  allowMultipleFiles?: boolean;
  maxSelectionCount?: number;
};

export type ImageSelectorRef = {
  openDevice: () => void;
  openLibrary: () => void;
  openCanva: () => void;
};

export function ImageSelector(props: ImageSelectorProps & RefAttributes<ImageSelectorRef>) {
  const {
    side = "front",
    title,
    onFileDrop,
    isLoggedIn: propIsLoggedIn,
    isDesktop: propIsDesktop,
    shouldOpenLibrary,
    onLibraryAutoOpenComplete,
    shouldOpenCanvaFromParent,
    onCanvaAutoOpenCompleteFromParent,
    ref,
    additionalButton,
    onCanvaFileReady,
    onLibraryDesignSelect,
    isWaitingForCatalogNumber = false,
    allowMultipleFiles = false,
    maxSelectionCount = 10
  } = props;
  const { isDesktop: responsiveIsDesktop } = useResponsive();
  const isDesktop = propIsDesktop ?? responsiveIsDesktop;
  const isLoggedIn = propIsLoggedIn ?? isUserLoggedIn();
  const { uploadArtwork, deleteArtwork } = useArtworkImage();
  const shouldShowAiStudio = useShowAiStudio();
  const shouldShowTakePhotoBtn = useShowTakePhotoBtn() ?? false;
  const shouldShowWeaveDesignLab = useShowWeaveDesignLab();
  const { getMachineBySide } = useArtwork();
  const machine = getMachineBySide(side);
  const { artworkId } = useEnhancedImageSource({ side });
  const { error: notifyError } = useNotification();
  const { tab } = useTabNavigation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Camera uploader ref
  const cameraUploaderRef = useRef<CameraUploader | null>(null);
  const takePhotoBtnContainerRef = useRef<HTMLDivElement | null>(null);

  const [isMyDesignLibraryOpen, setIsMyDesignLibraryOpen] = useState(false);
  const [shouldOpenCanva, setShouldOpenCanva] = useState(false);

  const onArtworkRemove = useCallback(() => {
    deleteArtwork(side, artworkId);
  }, [deleteArtwork, side, artworkId]);

  useAutoUploadDetection();

  const handleFileDrop = useCallback(
    async (droppedFiles: File[]) => {
      const file = droppedFiles[0];
      if (!file) return;

      const { valid, message } = await validateDesignLabArtworkFile(file);
      if (!valid) {
        notifyError({ title: "Upload Failed", description: message });
        return;
      }

      if (onFileDrop) {
        await onFileDrop(droppedFiles);
      } else {
        uploadArtwork({ file, side });
      }
    },
    [uploadArtwork, side, notifyError, onFileDrop]
  );

  const handleCameraFileDrop = useCallback(
    async (file: File) => {
      const { valid, message } = await validateDesignLabArtworkFile(file);
      if (!valid) {
        notifyError({ title: "Upload Failed", description: message });
        return;
      }

      uploadArtwork({ file, side, source: "camera" });
    },
    [uploadArtwork, side, notifyError]
  );

  const handleTakePhotoClick = useCallback(() => {
    trackTakePhotoCtaClicked(side);
  }, [side]);

  const handleDesignWithAiClick = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", "upload");
    newParams.set("design-menu", MENU_IDS.AI_DESIGN);
    setSearchParams(newParams, { replace: true });
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: DESIGN_LAB_ACCEPT_CONFIG,
    multiple: allowMultipleFiles,
    noClick: true,
    noKeyboard: true,
    onDrop: handleFileDrop
  });

  // Expose methods via imperative handle
  useImperativeHandle(
    ref,
    () => ({
      openDevice: open,
      openLibrary: () => setIsMyDesignLibraryOpen(true),
      openCanva: () => setShouldOpenCanva(true)
    }),
    [open]
  );

  // Handle auto-open library
  useEffect(() => {
    if (shouldOpenLibrary && isLoggedIn) {
      setIsMyDesignLibraryOpen(true);
      onLibraryAutoOpenComplete?.();
    }
  }, [shouldOpenLibrary, isLoggedIn, onLibraryAutoOpenComplete]);

  // Handle auto-open canva from parent
  useEffect(() => {
    if (shouldOpenCanvaFromParent && isLoggedIn) {
      setShouldOpenCanva(true);
      onCanvaAutoOpenCompleteFromParent?.();
    }
  }, [shouldOpenCanvaFromParent, isLoggedIn, onCanvaAutoOpenCompleteFromParent]);

  const onCanvaAutoOpenComplete = () => {
    setShouldOpenCanva(false);
  };

  // Initialize CameraUploader only when Take a Photo CTA should be shown
  useEffect(() => {
    if (!shouldShowTakePhotoBtn || !takePhotoBtnContainerRef.current) {
      return undefined;
    }

    cameraUploaderRef.current = new (CameraUploader as any)({
      button: `.js-take-a-photo-button[data-side="${side}"]`,
      onImage: (payload: { file: File; url: string; revoke: () => void }) => {
        handleCameraFileDrop(payload.file);
      },
      onError: (error: any) => {
        if (error.code === "camera_permission_denied") {
          notifyError({
            title: "Camera Access Required",
            description:
              "Camera access is required to take photos. Please allow camera permissions and try again."
          });
        } else if (error.code === "camera_permission_permanently_denied") {
          notifyError({
            title: "Camera Access Required",
            description:
              "Camera access is required to take photos. Please enable camera permissions in your device settings and try again."
          });
        } else {
          notifyError({
            title: "Camera Error",
            description:
              "Unable to access camera. Please try again or use 'My Device' to upload a photo from your gallery."
          });
        }
      }
    });

    return () => {
      if (cameraUploaderRef.current) {
        try {
          cameraUploaderRef.current.destroy?.();
        } catch (error) {
          // Silent cleanup error
          window.Honeybadger?.notify("ImageSelector camera cleanup error", {
            params: {
              error: error instanceof Error ? error.message : String(error),
              error_object: error instanceof Error ? error.toString() : String(error),
              stack_trace: error instanceof Error ? error.stack : null,
              component: "ImageSelector",
              action: "cameraCleanup",
              side
            }
          });
        }
        cameraUploaderRef.current = null;
      }
    };
  }, [shouldShowTakePhotoBtn, handleCameraFileDrop, side]);

  const handleLibraryClose = useCallback(() => {
    setIsMyDesignLibraryOpen(false);
  }, []);

  const handleLibrarySelect = useCallback(
    (artworkIds: number[]) => {
      setIsMyDesignLibraryOpen(false);
      // For backward compatibility, if onLibraryDesignSelect expects a single ID
      if (artworkIds.length === 1 && artworkIds[0]) {
        onLibraryDesignSelect?.(artworkIds[0]);
      }
    },
    [onLibraryDesignSelect]
  );

  const handleCanvaFilesReady = useCallback(
    async (files: File[]) => {
      if (onCanvaFileReady) {
        if (files[0]) {
          await onCanvaFileReady(files[0]);
        }
      } else {
        if (!files[0]) return;
        const { valid, message } = await validateDesignLabArtworkFile(files[0]);
        if (!valid) {
          notifyError({ title: "Upload Failed", description: message });
          return;
        }
        uploadArtwork({ file: files[0], side });
      }
    },
    [onCanvaFileReady, uploadArtwork, side, notifyError]
  );

  // Design Lab specific logic - only show these states when not using generic props
  const { source } = useEnhancedImageSource({ side });
  const isFileUploaded = Boolean(machine.state.context.artworkId);
  const { file } = machine.state.context;
  const { isStartDesigningOpen } = useStartDesigning();
  const isStartDesigningShown = isStartDesigningOpen;

  // Show design lab specific states only when using the design lab functionality
  if (
    !onFileDrop &&
    !isStartDesigningShown &&
    machine.state.matches("finished") &&
    source.enhancedImage
  ) {
    return <ImageUploadedBanner image={{ url: source.enhancedImage.base64 }} side={side} />;
  }

  if (!onFileDrop && !isStartDesigningShown && isFileUploaded && file) {
    return <ImageUploadedBanner image={{ url: URL.createObjectURL(file) }} side={side} />;
  }

  if (!onFileDrop && !isStartDesigningShown && machine.state.hasTag("loading")) {
    return (
      <div className="flex flex-1 flex-col gap-2">
        <LoadingBar
          side={side}
          startTime={machine.state.context.startTime}
          estimatedDuration={ARTWORK_PROCESSING.ESTIMATED_DURATION}
        />
        {isFileUploaded && (
          <div className="flex justify-center">
            <Button
              data-testid="delete-image-button"
              variant="outline"
              size="sm"
              onPress={onArtworkRemove}
            >
              <Trash2 className="size-4 text-red-600" />
              Delete image
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <section
        onDragEnter={getRootProps().onDragEnter}
        onDragOver={getRootProps().onDragOver}
        onDrop={getRootProps().onDrop}
        className="flex h-max w-full flex-1 border-spacing-12 flex-col items-center justify-center gap-2 rounded-md bg-blue-50 p-3 lg:gap-4 lg:border-2 lg:border-dashed lg:border-blue-700"
      >
        <div className="flex items-center gap-2">
          <Shirt className="size-6" />
          {title && <p className="font-bold">{title}</p>}
        </div>
        {shouldShowWeaveDesignLab && (tab === "front" || tab === "back") && (
          <>
            <p className="font-medium">Create your own designs</p>
            <Button
              onPress={handleDesignWithAiClick}
              data-testid="design-with-ai-button-design-lab"
              size="lg"
              className="bg-cta-gradient max-w-55.5 flex w-full rounded-lg py-2"
            >
              <SparkleIcon variant="white" className="size-6" />
              Design with AI
            </Button>
          </>
        )}
        {shouldShowWeaveDesignLab && (tab === "front" || tab === "back") ? (
          <p className="mt-1 font-medium">Choose a design from</p>
        ) : (
          <p className="font-medium">Drag your design here or choose a design from</p>
        )}

        <div className="mx-auto grid max-w-md grid-cols-2 justify-items-center gap-3 lg:flex lg:max-w-full lg:flex-wrap lg:justify-center [&>*:nth-child(odd):last-child]:col-span-2">
          {shouldShowTakePhotoBtn && !isDesktop && (
            <div ref={takePhotoBtnContainerRef} className="w-full max-w-40">
              <Button
                data-testid="my-take-a-photo-button"
                size="lg"
                variant="outline"
                onPress={handleTakePhotoClick}
                className="js-take-a-photo-button text-black! w-full border border-black/20 bg-gray-100 py-2 text-sm transition-colors"
                data-side={side}
              >
                <Camera className="size-5" />
                Take a Photo
              </Button>
            </div>
          )}

          <Button
            data-testid="my-device-button"
            size="lg"
            onPress={open}
            className="relative w-full max-w-40 py-2 text-sm lg:shrink"
          >
            <input {...getInputProps()} />
            My Device
          </Button>

          {shouldShowAiStudio && <AiStudioButton side={side} />}
          {additionalButton}

          {isLoggedIn && (
            <>
              <Button
                data-testid="my-library-button"
                size="lg"
                variant="outline"
                onPress={() => setIsMyDesignLibraryOpen(true)}
                className="w-full max-w-40 border-black/20 py-2 text-sm lg:shrink"
              >
                My Design Library
              </Button>
              <CanvaButton
                autoOpen={shouldOpenCanva}
                onAutoOpenComplete={onCanvaAutoOpenComplete}
                onFilesReady={handleCanvaFilesReady}
              />
            </>
          )}
        </div>
      </section>
      {isMyDesignLibraryOpen && (
        <MyDesignLibrary
          isOpen={isMyDesignLibraryOpen}
          onClose={handleLibraryClose}
          onSelectArtworks={handleLibrarySelect}
          isWaitingForCatalogNumber={isWaitingForCatalogNumber}
          allowMultipleSelection={allowMultipleFiles}
          maxSelectionCount={maxSelectionCount}
        />
      )}
    </>
  );
}
